import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ScanLine, Printer, Eye, Search, Loader2 } from "lucide-react";
import { api, API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MarketplaceBadge, StatusBadge, DuplicateBadge } from "@/components/badges";
import { PrintDialog } from "@/components/PrintDialog";

const openPreview = (id) => {
  const token = localStorage.getItem("snd_token");
  window.open(`${API}/labels/${id}/pdf${token ? `?token=${token}` : ""}`, "_blank");
};

export default function ScanPage() {
  const inputRef = useRef(null);
  const [q, setQ] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [printIds, setPrintIds] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const lookup = async (term) => {
    const t = (term ?? q).trim();
    if (t.length < 4) return toast.error("Enter at least 4 characters");
    setBusy(true);
    try {
      const { data } = await api.get("/labels/lookup", { params: { q: t } });
      setResult(data);
      setHistory((h) => [{ q: t, count: data.count, at: new Date() }, ...h.filter((x) => x.q !== t)].slice(0, 8));
      if (data.count === 1 && data.labels[0].status === "pending") toast.success("Found 1 pending label");
      if (data.count === 0) toast.error(`No label found for “${t}”`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      setQ("");
      inputRef.current?.focus();
    }
  };

  return (
    <div className="space-y-6" data-testid="scan-page">
      <div>
        <div className="font-mono text-[11px] uppercase tracking-widest text-emerald-400">Scan or paste · tracking / order ID</div>
        <h1 className="font-heading text-3xl font-extrabold uppercase tracking-tight sm:text-4xl lg:text-5xl">Find a label</h1>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); lookup(); }} className="flex flex-wrap gap-2 rounded-md border-2 border-blue-500/40 bg-[#111827] p-4" data-testid="scan-form">
        <div className="relative min-w-[260px] flex-1">
          <ScanLine className="absolute left-3 top-3 h-6 w-6 text-blue-300" />
          <Input ref={inputRef} data-testid="scan-input" value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" placeholder="Scan barcode or paste tracking / order ID and press Enter"
            className="h-12 border-slate-700 bg-slate-950 pl-12 font-mono text-base" />
        </div>
        <Button type="submit" data-testid="scan-submit-button" disabled={busy} className="h-12 bg-blue-600 px-6 hover:bg-blue-700">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="mr-2 h-5 w-5" />} Find</Button>
      </form>

      {result && (
        <div className="space-y-3" data-testid="scan-results">
          <div className="font-mono text-xs text-slate-400">
            {result.count} result(s) for <span className="text-slate-200">{result.query}</span>
            {result.match === "partial" && <span className="ml-2 text-amber-300">(partial match)</span>}
            {result.match === "text" && <span className="ml-2 text-amber-300">(found inside label text)</span>}
          </div>
          {result.labels.map((l) => (
            <div key={l.id} data-testid={`scan-result-${l.id}`} className="rise grid gap-4 rounded-md border border-slate-800 bg-[#111827] p-5 md:grid-cols-[1fr_auto]">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <MarketplaceBadge marketplace={l.marketplace} />
                  <StatusBadge status={l.status} matched={l.matched} />
                  {l.duplicate && <DuplicateBadge />}
                </div>
                <div className="font-heading text-2xl font-bold tracking-tight"><span className="text-blue-300">{l.warehouse_id || "UNMATCHED"}</span> <span className="text-slate-500">→</span> {l.master_sku || l.sku_id || "SKU not found"}</div>
                {l.product_name && <div className="text-sm text-slate-300">{l.product_name} · qty {l.qty}</div>}
                <div className="grid gap-1 font-mono text-xs text-slate-400 sm:grid-cols-2">
                  <div>Order: <span className="text-slate-200">{l.order_id || "—"}</span></div>
                  <div>Tracking: <span className="text-slate-200">{l.tracking_id || "—"}</span></div>
                  <div>File: <span className="text-slate-200">{l.file_name}</span> · page {l.page_index + 1}</div>
                  <div>{l.status === "printed" ? <>Printed {l.printed_at ? new Date(l.printed_at).toLocaleString() : ""}{l.printed_by ? ` by ${l.printed_by}` : ""}</> : "Not printed yet"}</div>
                </div>
              </div>
              <div className="flex flex-row gap-2 md:flex-col">
                <Button data-testid={`scan-reprint-${l.id}`} className="bg-blue-600 hover:bg-blue-700" onClick={() => setPrintIds([l.id])}><Printer className="mr-2 h-4 w-4" /> {l.status === "printed" ? "Reprint" : "Print"}</Button>
                <Button data-testid={`scan-preview-${l.id}`} variant="outline" className="border-slate-700 bg-transparent hover:bg-slate-800" onClick={() => openPreview(l.id)}><Eye className="mr-2 h-4 w-4" /> Preview</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {history.length > 0 && (
        <div className="space-y-1" data-testid="scan-history">
          <div className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Recent scans</div>
          <div className="flex flex-wrap gap-2">
            {history.map((h) => (
              <button key={h.q} type="button" onClick={() => lookup(h.q)} className="rounded-sm border border-slate-800 bg-slate-900 px-2 py-1 font-mono text-xs text-slate-300 transition-colors hover:border-blue-500/50">
                {h.q} <span className="text-slate-500">· {h.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <PrintDialog labelIds={printIds} open={!!printIds} onOpenChange={(o) => !o && setPrintIds(null)} onCompleted={() => result && lookup(result.query)} />
    </div>
  );
}
